import {
  Attachment,
  MAX_TOTAL_ATTACHMENT_SIZE_BYTES,
  MAX_ATTACHMENT_COUNT,
  SUPPORTED_ATTACHMENT_EXTENSIONS
} from '../models/Attachment';
import { formatBytes } from './imageUploadUtils';

export interface AttachmentValidationResult {
  valid: boolean;
  error?: string;
  validFiles: File[];
}

const isValidFileType = (file: File): boolean => {
  const fileExtension = file.name.split('.').pop()?.toLowerCase();
  if (!fileExtension) return false;
  const extension = `.${fileExtension}`;
  return SUPPORTED_ATTACHMENT_EXTENSIONS.includes(extension);
};

export const validateAttachments = (
  newFiles: FileList | null,
  existingAttachments: Attachment[]
): AttachmentValidationResult => {
  if (!newFiles || newFiles.length === 0) {
    return { valid: false, error: 'No files selected', validFiles: [] };
  }

  const validFiles: File[] = [];
  const errors: string[] = [];

  const totalCount = existingAttachments.length + newFiles.length;
  if (totalCount > MAX_ATTACHMENT_COUNT) {
    return {
      valid: false,
      error: `You can only attach up to ${MAX_ATTACHMENT_COUNT} files.`,
      validFiles: []
    };
  }

  const existingTotalSize = existingAttachments.reduce((sum, attachment) => sum + attachment.size, 0);

  for (let i = 0; i < newFiles.length; i++) {
    const file = newFiles[i];

    if (!isValidFileType(file)) {
      errors.push(`"${file.name}" has an unsupported file type.`);
      continue;
    }

    const isDuplicate = existingAttachments.some(
      attachment => attachment.name === file.name && attachment.size === file.size
    );
    if (isDuplicate) {
      errors.push(`"${file.name}" is already attached.`);
      continue;
    }

    validFiles.push(file);
  }

  const newFilesTotalSize = validFiles.reduce((sum, file) => sum + file.size, 0);
  const projectedTotalSize = existingTotalSize + newFilesTotalSize;

  if (projectedTotalSize > MAX_TOTAL_ATTACHMENT_SIZE_BYTES) {
    return {
      valid: false,
      error: `Total size of attachments would exceed the maximum of ${formatBytes(MAX_TOTAL_ATTACHMENT_SIZE_BYTES)}.`,
      validFiles: []
    };
  }

  if (errors.length > 0) {
    return {
      valid: false,
      error: errors.join(' '),
      validFiles
    };
  }

  return { valid: true, validFiles };
};

export const createAttachmentFromFile = (file: File): Attachment => {
  return {
    id: crypto.randomUUID(),
    name: file.name,
    size: file.size,
    file
  };
};
